import { useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Stack,
  Text,
} from "@/components/ui";
import type { CentroDeCostoConOrganizaciones } from "@/lib/types";
import type { EditarCecoInput } from "../services/catalogos-contables-admin-service";
import { OrganizacionesPicker } from "./OrganizacionesPicker";

/**
 * Modal de edición de un CeCo · nombre + organización(es). El código es inmutable
 * (llave funcional). Al guardar se sincroniza el conjunto de organizaciones.
 */
export function CecoEditarDialog({
  ceco,
  open,
  onClose,
  onGuardar,
  isPending,
  etiqueta = "CeCo",
  nota = "El código del centro de costo no se puede cambiar. Un CeCo puede pertenecer a varias organizaciones.",
}: {
  /** Etiqueta corta del título («CeCo», «cuenta»). */
  etiqueta?: string;
  nota?: string;
  ceco: CentroDeCostoConOrganizaciones;
  open: boolean;
  onClose: () => void;
  onGuardar: (args: { id: string; input: EditarCecoInput }) => void;
  isPending: boolean;
}) {
  const [nombre, setNombre] = useState(ceco.nombre);
  const [numerosSociedad, setNumerosSociedad] = useState<string[]>(
    ceco.organizaciones.map((o) => o.numeroSociedad)
  );
  const [errorOrg, setErrorOrg] = useState<string>();

  const guardar = () => {
    if (!nombre.trim()) return;
    if (numerosSociedad.length === 0) {
      setErrorOrg("Selecciona al menos una organización.");
      return;
    }
    onGuardar({ id: ceco.id, input: { nombre: nombre.trim(), numerosSociedad } });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Editar {etiqueta} <span className="font-mono text-sm text-muted-foreground">{ceco.codigo}</span>
          </DialogTitle>
        </DialogHeader>
        <Stack gap="md">
          <Input label="Nombre" value={nombre} onChange={(e) => setNombre(e.currentTarget.value)} maxLength={160} required />
          <OrganizacionesPicker
            value={numerosSociedad}
            onChange={(v) => {
              setNumerosSociedad(v);
              if (v.length > 0) setErrorOrg(undefined);
            }}
            error={errorOrg}
          />
          <Text size="xs" c="dimmed">
            {nota}
          </Text>
        </Stack>
        <DialogFooter>
          <Button variant="light" color="slate" onClick={onClose} disabled={isPending}>
            Cancelar
          </Button>
          <Button onClick={guardar} loading={isPending}>
            Guardar cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
